import { useEffect, useMemo, useState } from "react";
import {
  MissionPlayer,
  postReady,
  type AttemptEnvelope,
  type MissionPayload,
} from "@ahimsa/aletheia-runner";
import {
  fetchMission,
  postAttempt,
  readConfigFromQuery,
  type MissionDTO,
  type MissionRevisionDTO,
} from "./api";

import "./styles.css";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; mission: MissionDTO; revision: MissionRevisionDTO }
  | { status: "error"; message: string };

export function App() {
  const parsed = useMemo(() => readConfigFromQuery(window.location.search), []);
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [lastAttempt, setLastAttempt] = useState<AttemptEnvelope | null>(null);

  useEffect(() => {
    postReady();
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function go() {
      if (!parsed.missionId) {
        setLoad({
          status: "error",
          message:
            "Falta el parámetro ?mission_id=<uuid> en la URL. La app Swift " +
            "lo inyecta automáticamente.",
        });
        return;
      }
      try {
        const mission = await fetchMission(parsed.config, parsed.missionId);
        const candidates = mission.revisions ?? [];
        const explicit = parsed.revisionId
          ? candidates.find((r) => r.id === parsed.revisionId)
          : null;
        const chosen =
          explicit ??
          mission.current_revision ??
          candidates.find((r) => r.is_published) ??
          candidates[0];
        if (!chosen) {
          if (!cancelled) {
            setLoad({
              status: "error",
              message: `La misión ${mission.slug} no tiene revisión publicada.`,
            });
          }
          return;
        }
        if (!cancelled) {
          setLoad({ status: "ready", mission, revision: chosen });
        }
      } catch (err) {
        if (!cancelled) {
          setLoad({ status: "error", message: String(err) });
        }
      }
    }
    void go();
    return () => {
      cancelled = true;
    };
  }, [parsed]);

  async function handleAttempt(envelope: AttemptEnvelope) {
    setLastAttempt(envelope);
    if (load.status !== "ready") return;
    if (!parsed.config.token) {
      // Standalone dev: just log. Host-driven runs always carry a token.
      // eslint-disable-next-line no-console
      console.info("[aletheia-runner] dev mode — attempt:", envelope);
      return;
    }
    try {
      const now = new Date();
      const startedAt = new Date(now.getTime() - envelope.duration_ms);
      await postAttempt(parsed.config, {
        client_event_id: envelope.client_event_id,
        mission_id: envelope.mission_public_id,
        mission_revision_id: envelope.mission_revision_public_id,
        started_at: startedAt.toISOString(),
        ended_at: now.toISOString(),
        duration_ms: envelope.duration_ms,
        outcome: envelope.passed ? "completed" : "partial",
        score: envelope.score,
        client_payload: envelope.raw_response ?? {},
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[aletheia-runner] failed to post attempt:", err);
    }
  }

  if (load.status === "loading") {
    return <div className="splash">Cargando misión…</div>;
  }
  if (load.status === "error") {
    return (
      <div className="splash splashError">
        <strong>Algo salió mal</strong>
        <p>{load.message}</p>
      </div>
    );
  }

  const payload = load.revision.payload as MissionPayload;
  return (
    <div className="appShell">
      <header className="appHeader">
        <span className="appHeaderTitle">{load.mission.title}</span>
        <span className="appHeaderKind">{load.mission.activity_kind}</span>
      </header>
      <MissionPlayer
        payload={payload}
        context={{
          mission_public_id: load.mission.public_id,
          mission_revision_public_id: load.revision.id,
        }}
        onAttempt={(env) => {
          void handleAttempt(env);
        }}
      />
      {lastAttempt && (
        <footer className="appFooter">
          Último envío — score{" "}
          <strong>{Math.round(lastAttempt.score * 100)}%</strong>
          {lastAttempt.passed ? " ✓" : " ✗"}
        </footer>
      )}
    </div>
  );
}
